/**
 * ID21 — 해외 배송비 설정 FE 타입. (be-order 계약 — 도착 시 정합.)
 * 상점별(영문/중문/일문) 해외 배송조건(무게/박스/EMS·SELF/보험/국가그룹 요율)을 관리한다.
 * 설정 + 계산 계약(seam)까지 — 실제 외화 결제 체크아웃 연동은 본 항목 범위 밖.
 */

import type { RangeRepeat } from '@/lib/domain/rangeRepeat';

/** 해외 상점 스코프 (KRW 기준 국문 BASE 는 해외배송 비대상). */
export type OverseasStoreScope = 'EN' | 'CN' | 'JP';

export const OVERSEAS_STORE_SCOPES: ReadonlyArray<OverseasStoreScope> = ['EN', 'CN', 'JP'];

export const OVERSEAS_STORE_LABELS: Record<OverseasStoreScope, string> = {
  EN: '영문',
  CN: '중문',
  JP: '일문',
};

/** 국기 이모지(목록 시인성). */
export const OVERSEAS_STORE_FLAGS: Record<OverseasStoreScope, string> = {
  EN: '🇺🇸',
  CN: '🇨🇳',
  JP: '🇯🇵',
};

/** 배송 기준. SELF=자체 요율표, EMS=우체국 EMS 표준요율. */
export type ShippingBasis = 'SELF' | 'EMS';

export const SHIPPING_BASES: ReadonlyArray<ShippingBasis> = ['SELF', 'EMS'];

export const SHIPPING_BASIS_LABELS: Record<ShippingBasis, string> = {
  SELF: '자체 요율',
  EMS: 'EMS 표준',
};

/**
 * EMS 표준요율 권역 번호.
 *
 * 고도몰 EMS 권역이 정본이다(이관팀·BE 합의 — 라이브 계산식과 같은 30권역 × 63무게구간). 이관본의
 * `ems_standard_rates.zone` 과 EMS 국가그룹 `groupName` 이 이 번호를 그대로 쓴다(예: '15' = US).
 * basis=EMS 일 때 group_name 이 요율 조회 키라(be-order `resolveEmsFee(group.getGroupName())`)
 * **요율표에 있는 값이어야 계산된다.**
 *
 * ★임시 목록이다. BE 가 요율표의 zone 목록 조회 API 를 만들기로 했다 — 나오면 이 상수 대신 그 응답으로
 * 선택지와 검증을 만든다. 이관 전 임시값이던 ASIA / NORTH_AMERICA / EUROPE / OCEANIA 는 새 요율표에
 * 없어 뺐다(고르면 계산이 실패한다).
 */
export const EMS_ZONES: ReadonlyArray<string> = [
  '1', '2', '3', '4',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
  '21', '22', '23', '24', '25', '26', '27', '28', '29', '30',
];

/** 그룹명이 요율표에 있는 EMS 권역인지 — 없는 값으로 저장하면 배송비가 산정되지 않는다(BREAK-3). */
export const isEmsZone = (value: string): boolean => EMS_ZONES.includes(value);

/**
 * EMS 추가요금 단위 — 고도몰 해외배송 정책(몰별)의 "EMS 추가요금" 이관.
 * WON = 원화 금액, PERCENT = EMS 표준요율 대비 %. be-order `EmsAddCostUnit`(V60), 기본값 WON.
 * 금액 컬럼은 `ems_add_cost DECIMAL(12,2)` 다.
 */
export type EmsAddCostUnit = 'WON' | 'PERCENT';

export const EMS_ADD_COST_UNITS: ReadonlyArray<EmsAddCostUnit> = ['WON', 'PERCENT'];

/** 무게 구간 요율 (overseas_shipping_rates). SELF basis 일 때 사용. */
export interface OverseasShippingRate {
  /** 기존 행이면 서버 id, 신규면 undefined. */
  id?: string;
  weightFromKg: number;
  weightToKg: number;
  /** 정액 요율(원). 0 가능. per-gram 만 쓸 땐 0. */
  feeKrw: number;
  /**
   * R4 163 — g당 소수점 단가(원/g, DECIMAL(10,4) 소수4자리). be-order RateUpsert.perGramRateKrw.
   * null/미설정 = 정액(feeKrw)만 사용. 값 있으면(>0) BE 가 요금=무게×단가로 산정(feeKrw 무시).
   * ★BE 검증: feeKrw(≥0)·perGramRateKrw(>0) 둘 다 없으면 400.
   */
  perGramRateKrw?: number | null;
}

/**
 * 지역 추가배송비 (overseas_shipping_region_surcharges, order V62) — 자체요율(SELF) 국가그룹 단위.
 * 배송지 주소에 지역명이 들어 있으면 가산하고, 여러 지역이 맞으면 가장 구체적인(긴) 1건만 적용한다.
 * 예) 중문몰 "한국" 그룹 — 제주·도서산간 등 247곳.
 */
export interface OverseasRegionSurcharge {
  /** 조회 응답에만 있다. */
  id?: string;
  /** 지역명. 필수, 최대 200자. */
  areaName: string;
  /** 추가요금(원). 0 이상 정수. */
  surchargeKrw: number;
}

/** 국가 그룹 (overseas_shipping_country_groups). 정책과 1:1 매칭 동적 테이블. */
export interface OverseasCountryGroup {
  groupId?: string;
  /**
   * 권역명. SELF=자유 텍스트(예 중국-기타/미주). ★EMS=EMS zone enum(ASIA/NORTH_AMERICA/...)
   * 과 일치해야 표준요율이 적용된다(be-order 가 group_name 을 zone 키로 사용).
   */
  groupName: string;
  conditionName: string;
  /** 그룹 정렬 순서(be-order). 제출 시 배열 인덱스로 채운다. */
  sortOrder?: number;
  /**
   * QA-119 — 이 권역에 매핑할 ISO2 국가코드(대문자, 예 ["US","CA"]). be-order CountryGroupUpsert.
   * 배송지 country→group 역매칭에 사용. 빈 배열=미매핑(단일그룹 폴백). ★편집 시 반드시 round-trip
   * 해야 함 — 누락하면 PUT(replace-all)이 기존 매핑을 지운다.
   */
  countryCodes: string[];
  rates: OverseasShippingRate[];
  /**
   * 범위반복(고도몰 rangeRepeat, order V61) — 첫 구간 요금 + 시작(kg)부터 단위(kg)마다 추가요금(원).
   * null/미존재 = 반복 없음(반복이 없으면 조회 응답에서 필드가 빠진다). **자체요율(SELF)에서만** 쓰이고
   * EMS 정책이면 BE 가 무시한다. ★PUT 이 전체 교체라 저장 때마다 반드시 함께 보내야 한다 — 빼면 해제된다.
   */
  rangeRepeat?: RangeRepeat | null;
  /**
   * 지역 추가배송비(order V62). **자체요율(SELF)에서만** 쓰이고 EMS 면 BE 가 저장하지 않는다. V62 이전
   * 응답에는 없다. ★PUT 이 전체 교체라 저장 때마다 반드시 함께 보내야 한다 — 빼면 기존 목록이 지워진다.
   */
  regionSurcharges?: OverseasRegionSurcharge[];
}

/** 해외 배송 정책 (overseas_shipping_policies). */
export interface OverseasShippingPolicy {
  policyId: string;
  storeScope: OverseasStoreScope;
  basis: ShippingBasis;
  /** 기준 상품 무게(kg, 0.01 단위). */
  baseItemWeightKg: number;
  /** 박스 무게(kg). */
  boxWeightKg: number;
  /**
   * 최대 배송 무게(kg). be-order OverseasPolicyResponse.maxWeightKg (V58).
   * null/미설정 = 무제한. 값 있으면 BE 가 초과 해외 주문을 422 로 차단한다.
   */
  maxWeightKg: number | null;
  /**
   * EMS 추가요금 — basis=EMS 일 때 EMS 표준요율에 더해 청구된다. 단위는 `emsAddCostUnit`.
   * 선택 필드인 이유: V60 이전 응답에는 없다. 없거나 null 이면 0 · WON 으로 본다(BE 정규화와 동일).
   */
  emsAddCost?: number | null;
  emsAddCostUnit?: EmsAddCostUnit | null;
  insuranceRequired: boolean;
  /** 보험 자동적용 금액 구간(자유 문자열, 예: "10만원 이상"). */
  insuranceAutoRange: string | null;
  active: boolean;
  countryGroups: OverseasCountryGroup[];
}

/** POST/PUT body (OverseasShippingPolicyRequest). 정책+국가그룹+요율 중첩. */
export interface OverseasShippingPolicyPayload {
  storeScope: OverseasStoreScope;
  basis: ShippingBasis;
  baseItemWeightKg: number;
  boxWeightKg: number;
  /** 최대 배송 무게(kg). null = 무제한. be-order OverseasPolicyUpsertRequest.maxWeightKg(@PositiveOrZero, nullable). */
  maxWeightKg: number | null;
  /**
   * EMS 추가요금. basis 와 무관하게 **항상 보낸다** — 정책 PUT 은 전체 교체라, 자체요율(SELF)이라
   * 입력란을 숨겼다고 빼면 저장된 값이 지워진다. SELF 로 바꿨다가 EMS 로 되돌려도 값이 남아야 한다.
   */
  emsAddCost: number;
  emsAddCostUnit: EmsAddCostUnit;
  insuranceRequired: boolean;
  insuranceAutoRange: string | null;
  active: boolean;
  countryGroups: OverseasCountryGroup[];
}
